import { api } from '@/lib/api';

export type ProductBranding = {
  slug: string;
  name: string;
  tagline: string;
  icon: string;
  accentGradient: string;
  features: string[];
};

/**
 * Legacy slug mapping — maps old frontend slugs to canonical registry product names.
 * Used for backward compat with bookmarks, stored localStorage values, etc.
 */
const LEGACY_SLUG_MAP: Record<string, string> = {
  finops: 'billing',
  otel: 'observability',
  rms: 'caspeco',
};

/** Resolve a slug to canonical form (handles legacy slugs) */
function canonicalSlug(slug: string): string {
  return LEGACY_SLUG_MAP[slug] || slug;
}

/** Dynamic product map — populated from the API */
let dynamicProducts: Record<string, ProductBranding> | null = null;
let fetchPromise: Promise<void> | null = null;

/** Fetch products from API and populate the dynamic map */
export async function fetchProducts(): Promise<Record<string, ProductBranding>> {
  if (dynamicProducts) return dynamicProducts;
  if (fetchPromise) {
    await fetchPromise;
    return dynamicProducts || {};
  }

  fetchPromise = (async () => {
    try {
      const { data, error } = await api.api.v1.products.get();
      if (error || !data || !('products' in data)) return;

      const map: Record<string, ProductBranding> = {};
      for (const p of data.products) {
        map[p.name] = {
          slug: p.name,
          name: p.display_name,
          tagline: p.tagline,
          icon: p.icon,
          accentGradient: p.accent_gradient,
          features: p.features,
        };
      }
      if (Object.keys(map).length > 0) {
        dynamicProducts = map;
      }
    } catch {
      // silently fail — products won't be available until API responds
    }
  })();

  await fetchPromise;
  fetchPromise = null;
  return dynamicProducts || {};
}

export function getProductBranding(slug: string): ProductBranding | undefined {
  const canonical = canonicalSlug(slug);
  return dynamicProducts?.[canonical];
}

export function getAllProducts(): ProductBranding[] {
  return Object.values(dynamicProducts || {});
}
